import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { showToast } from '../../components/shared';
import { ArrowLeft, Mail } from 'lucide-react';

// Forgot Password — v2 design (2026-05-30).
//
// Layout:
//   - Lavender background (same wash as Login).
//   - Back arrow top-left → /login.
//   - Inline lock-with-question-mark illustration (CSS/SVG; the empty-state
//     PNGs don't include this specific motif so we draw it).
//   - Heading "Forgot Password?"
//   - One-line explainer.
//   - Email input.
//   - Indigo gradient "Send Reset Link" button.
//   - "Back to Login" text link.

const BRAND = '#5B47E0';
const BRAND_DARK = '#4A37CC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';

const ForgotPasswordScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSend = () => {
    if (!email.trim() || !emailRegex.test(email.trim())) {
      showToast('Please enter a valid email', 'error');
      return;
    }
    setSending(true);
    userStore.forgotPassword(email.trim(), (success, message) => {
      setSending(false);
      if (success) {
        showToast(message || 'Reset link sent', 'success');
        // Empty the field but stay on the page so the user sees the
        // success toast and can re-send if needed.
        setEmail('');
      } else {
        showToast(message || 'Failed to send reset email', 'error');
      }
    });
  };

  return (
    <div
      className="h-full relative overflow-y-auto"
      style={{
        background:
          'linear-gradient(180deg, #F8F5FE 0%, #EEEAFB 50%, #E5DFF9 100%)',
      }}
    >
      {/* Top bar with back arrow */}
      <div className="safe-area-top">
        <div className="px-5 pt-4 pb-2">
          <button
            onClick={() => navigate('/login')}
            aria-label="Back"
            className="w-10 h-10 rounded-full bg-white flex items-center justify-center transition-colors"
            style={{ border: '1px solid #E2DFF5' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: TEXT_PRIMARY }} />
          </button>
        </div>
      </div>

      <div className="px-6 pt-6 pb-10">
        {/* === Illustration === */}
        <div className="flex flex-col items-center mb-8">
          <LockWithQuestion />
        </div>

        {/* === Heading === */}
        <div className="text-center mb-8 max-w-sm mx-auto">
          <h1
            className="text-[28px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY, lineHeight: 1.15 }}
          >
            Forgot Password?
          </h1>
          <p
            className="text-[15px] mt-3 leading-relaxed"
            style={{ color: TEXT_MUTED }}
          >
            No worries! Enter your email address and we&apos;ll send you a
            link to reset your password.
          </p>
        </div>

        {/* === Form === */}
        <div className="w-full max-w-sm mx-auto space-y-4">
          <div
            className="relative h-12 rounded-2xl flex items-center px-4"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2DFF5',
            }}
          >
            <Mail
              className="w-[18px] h-[18px] mr-3 flex-shrink-0"
              style={{ color: TEXT_MUTED }}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              inputMode="email"
              className="flex-1 bg-transparent outline-none text-[15px]"
              style={{ color: TEXT_PRIMARY }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
          </div>

          <button
            onClick={handleSend}
            disabled={sending}
            className="w-full h-12 rounded-2xl text-white text-[16px] font-semibold transition-transform active:scale-[0.98] disabled:opacity-60"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
              boxShadow: '0 12px 28px -8px rgba(91, 71, 224, 0.5)',
            }}
          >
            {sending ? 'Sending...' : 'Send Reset Link'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full text-center text-[14px] font-semibold pt-2"
            style={{ color: BRAND }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
});

/* ====================================================================
 *  LockWithQuestion — inline SVG illustration.
 *
 *  Drawn rather than sourced from the empty-state PNGs because none of
 *  those (no_library, no_wifi, etc.) match the "lock with question mark"
 *  motif Mike's design specifies. Colors match the indigo system.
 * ==================================================================== */

function LockWithQuestion() {
  return (
    <div
      className="relative w-[140px] h-[140px] rounded-[36px] flex items-center justify-center"
      style={{
        background:
          'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(232,228,251,1))',
        boxShadow: '0 20px 50px -10px rgba(91, 71, 224, 0.25)',
      }}
    >
      <svg viewBox="0 0 120 120" className="w-[88px] h-[88px]" aria-hidden="true">
        {/* Lock body */}
        <rect
          x="28"
          y="50"
          width="64"
          height="58"
          rx="14"
          fill="#A89AF0"
        />
        {/* Lock shackle */}
        <path
          d="M 42 50 V 38 a 18 18 0 0 1 36 0 V 50"
          fill="none"
          stroke="#8B7BE5"
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Inner highlight */}
        <rect
          x="34"
          y="56"
          width="52"
          height="46"
          rx="10"
          fill="#5B47E0"
          opacity="0.15"
        />
      </svg>

      {/* Question-mark badge — top-right overlapping the lock */}
      <div
        className="absolute -top-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          background: '#5B47E0',
          boxShadow: '0 8px 16px -4px rgba(91, 71, 224, 0.5)',
        }}
      >
        <span className="text-white text-[22px] font-bold leading-none">?</span>
      </div>

      {/* Sparkle accents */}
      <span className="absolute top-3 left-3 text-[#8B7BE5] text-sm" aria-hidden="true">✦</span>
      <span className="absolute bottom-5 right-7 text-[#A89AF0] text-xs" aria-hidden="true">✦</span>
      <span className="absolute top-12 -left-1 text-[#5B47E0] text-[10px] opacity-60" aria-hidden="true">✦</span>
    </div>
  );
}

export default ForgotPasswordScreen;
